import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const DateDrawer = ({ isOpen, onClose, onSave, initialDate }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [hours, setHours] = useState('12');
  const [minutes, setMinutes] = useState('00');
  const [ampm, setAmpm] = useState('AM');

  useEffect(() => {
    if (initialDate) {
      const date = new Date(initialDate);
      setSelectedDate(date.toISOString().split('T')[0]);
      
      // Convert to 12-hour format
      let hrs = date.getHours();
      const isAM = hrs < 12;
      hrs = hrs % 12;
      hrs = hrs ? hrs : 12;
      
      setHours(String(hrs).padStart(2, '0'));
      setMinutes(String(date.getMinutes()).padStart(2, '0'));
      setAmpm(isAM ? 'AM' : 'PM');
    }
  }, [initialDate]);

  const handleSave = () => {
    if (selectedDate && hours && minutes) {
      // Convert 12-hour time to 24-hour time for ISO string
      let hour24 = parseInt(hours);
      if (ampm === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (ampm === 'AM' && hour24 === 12) {
        hour24 = 0;
      }

      const timeStr = `${String(hour24).padStart(2, '0')}:${minutes}:00`;
      const dateTime = new Date(`${selectedDate}T${timeStr}`);
      onSave(dateTime.toISOString());
    }
  };

  // Add format helper function
  const formatPreviewDate = () => {
    if (!selectedDate || !hours || !minutes) return 'Please select both date and time';
    
    const date = new Date(selectedDate);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    
    return `${dd}/${mm}/${yy}  ${hours}:${minutes} ${ampm}`;
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity z-40"
          onClick={onClose}
        />
      )}  
      <div
        className={`fixed inset-y-0 right-0 w-96 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-4 py-6 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Configure Wait Date</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Date
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                />
              </div>

              {/* Time Selection with AM/PM */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time
                </label>
                <div className="flex gap-2">
                  {/* Hours */}
                  <select
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                  >
                    {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
                      <option key={h} value={String(h).padStart(2, '0')}>
                        {String(h).padStart(2, '0')}
                      </option>
                    ))}
                  </select>

                  <span className="flex items-center">:</span>

                  {/* Minutes */}
                  <select
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={String(i).padStart(2, '0')}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>

                  {/* AM/PM */}
                  <select
                    value={ampm}
                    onChange={(e) => setAmpm(e.target.value)}
                    className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-[var(--brand-primary)] focus:ring-[var(--brand-primary)] sm:text-sm"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
                <p className="text-sm text-gray-600 font-mono">
                  {formatPreviewDate()}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!selectedDate || !hours || !minutes}
                className="px-4 py-2 text-sm font-medium text-white bg-[var(--brand-primary)] border border-transparent rounded-md shadow-sm hover:bg-[#3aa5b9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--brand-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DateDrawer; 